import { Site } from '@dustin-web/microsite-config/src/site-context'
import {
  AccountAndPrivacyTermsState,
  SourcingCompany,
} from './graphql/generated/graphql-types'
import { RefObject, useEffect } from 'react'
import Cookies from 'universal-cookie'

const setCookieValue = (name: string, value: string) => {
  const oneYearFromNow = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365)

  const cookies = new Cookies()

  cookies.set(name, value, {
    httpOnly: false,
    sameSite: 'lax',
    secure: false,
    path: '/',
    expires: oneYearFromNow,
  })
}

const removeCookie = (name: string) => {
  const cookies = new Cookies()

  cookies.remove(name)
}

const getNames = (name: string | undefined) => {
  try {
    const names = name?.split(' ')

    return {
      firstName: names ? names[0] : '',
      lastName: names && names.length >= 1 ? names[1] : '',
    }
  } catch (error) {
    return {
      firstName: '',
      lastName: '',
    }
  }
}

const getSourcingCompany = (sourcingCompany: string): SourcingCompany => {
  if (sourcingCompany === 'SE') return SourcingCompany.Se
  if (sourcingCompany === 'DK') return SourcingCompany.Dk
  if (sourcingCompany === 'NO') return SourcingCompany.No
  if (sourcingCompany === 'FI') return SourcingCompany.Fi
  if (sourcingCompany === 'NL') return SourcingCompany.Nl

  return SourcingCompany.None
}

const getAccountAndPrivacyTermsState = (state: string) => {
  switch (state) {
    case 'ACCEPTED':
      return AccountAndPrivacyTermsState.Accepted
    case 'NOT_ACCEPTED':
      return AccountAndPrivacyTermsState.NotAccepted
    case 'NEEDS_TO_ACCEPT_PRIVACY_TERMS_UPDATE':
      return AccountAndPrivacyTermsState.NeedsToAcceptPrivacyTermsUpdate
    default:
      return AccountAndPrivacyTermsState.None
  }
}

type AnyEvent = MouseEvent | TouchEvent

const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: AnyEvent) => void
) => {
  useEffect(() => {
    const listener = (event: AnyEvent) => {
      const el = ref?.current

      // Do nothing if clicking ref's element or descendent elements
      if (!el || el.contains(event.target as Node)) {
        return
      }

      handler(event)
    }

    document.addEventListener(`mousedown`, listener)
    document.addEventListener(`touchstart`, listener)

    return () => {
      document.removeEventListener(`mousedown`, listener)
      document.removeEventListener(`touchstart`, listener)
    }

    // Reload only if ref or handler changes
  }, [ref, handler])
}

const getCookieValue = (cookieName: string, cookie: string | undefined) => {
  const cs = cookie?.split(';') ?? []

  for (const c of cs) {
    const cx = c.trim().split('=')

    if (cx[0] === cookieName) {
      return cx[1]
    }
  }

  return null
}

const concatWithSeparator = (separator: string, ...args: string[]) => {
  let result: string = ''

  for (let i = 0; i < args.length; i++) {
    const element = args[i]

    result = `${result}${element}`

    if (args[i + 1]) {
      result = `${result}${separator}`
    }
  }

  return result
}

// Unified function to get string from potentially string array or undefined
const getStringFromUrl = (param: string | string[] | undefined): string | null => {
  if (typeof param === 'string') {
    return param;
  }
  return null;
};

// Wrapper functions for specific parameters
const getErpFromUrl = (uid: string | string[] | undefined): string | null => {
  return getStringFromUrl(uid);
};

const getCustomerErpFromUrl = (cid: string | string[] | undefined): string | null => {
  return getStringFromUrl(cid);
};

const getAccountTermsUrl = (site: Site) => {
  switch (site) {
    case 'DustinFinland':
      return '/service/dustintili'
    case 'DustinNetherlands':
      return '/winkelen-bij-dustin/accountvoorwaarden'
    case 'DustinSweden':
    case 'DustinNorway':
    case 'DustinDenmark':
      return '/service/dustinkonto'
  }
}

const getPrivacyTermsUpdateUrl = () => {
  return 'https://dustinweb.azureedge.net/content/60343/se-privacy-policy-en-master-october-2019.pdf'
}

export {
  getNames,
  useOnClickOutside,
  getCookieValue,
  getSourcingCompany,
  concatWithSeparator,
  getErpFromUrl,
  setCookieValue,
  getAccountAndPrivacyTermsState,
  getAccountTermsUrl,
  getPrivacyTermsUpdateUrl,
  removeCookie,
  getCustomerErpFromUrl
}
